import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { Edit, KeyboardBackspace } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from 'routes/constants';
import { EasTooltip } from 'ui/EasTooltip';
import {
	clearStoreSettings,
	getStoreSettingsRequest,
	listSalesChannelsCatalogRequest,
} from 'redux/reducers/shopifyStores/reducer';
import {
	selectSalesChannelsCatalogData,
	selectStoreSettings,
	selectStoreSettingsLoading,
} from 'redux/reducers/shopifyStores/selectors';
import { getSavedUrl2 } from 'redux/reducers/auth/selectors';
import { EasParamsBox } from '../../../EasParamsBox';
import { EditShopifyStoreDialog } from '../EditShopifyStoreDialog';
import {
	applicationBoxStructure,
	easAuthBoxStructure,
	easParametersBoxStructure,
	salesChannelsBoxStructure,
	storeBoxStructure,
	TShopifyStoreFlat,
} from './boxStructures';
import { flattenStoreSettings } from './utils';

export const ShopifyStoreDetails: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { emId, storeId } = useParams<{ emId?: string; storeId?: string }>();
	const settings = useSelector(selectStoreSettings);
	const loading = useSelector(selectStoreSettingsLoading);
	const salesChannelsCatalog = useSelector(selectSalesChannelsCatalogData);
	const savedUrl2 = useSelector(getSavedUrl2);

	const [isEditOpen, setIsEditOpen] = useState(false);

	useEffect(() => {
		if (storeId) {
			dispatch(getStoreSettingsRequest({ store_id: Number(storeId) }));
		}
		dispatch(
			listSalesChannelsCatalogRequest({
				pageSize: 100,
				pageNumber: 1,
				searchString: '',
				filters: [{ fieldName: 'isActive', value: true, operator: '=' }],
			}),
		);
		return () => {
			dispatch(clearStoreSettings());
		};
	}, [dispatch, storeId]);

	const flat = useMemo<TShopifyStoreFlat | null>(
		() => flattenStoreSettings(settings, salesChannelsCatalog),
		[settings, salesChannelsCatalog],
	);

	const handleBack = () => {
		if (savedUrl2 && savedUrl2.includes(PATHS.EM_MANAGEMENT_PAGE)) {
			navigate(savedUrl2);
		} else if (emId) {
			navigate(PATHS.EM_MANAGEMENT_ID_PAGE.replace(':emId', emId));
		} else {
			navigate(PATHS.EM_MANAGEMENT_PAGE);
		}
	};

	const title = settings?.store?.name ? `Shopify Store – ${settings.store.name}` : 'Shopify Store';

	const isInitialLoading = loading && !settings;
	const storeIdNum = storeId ? Number(storeId) : null;

	return (
		<Box sx={{ padding: '0px 10px 0 10px', maxWidth: '100vw', overflowX: 'auto', height: '100%' }}>
			<Box sx={{ minWidth: '1100px', width: '100%', height: '100%' }}>
				<Box
					sx={{
						m: '0 10px 10px 10px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<EasTooltip title="Back">
							<IconButton onClick={handleBack} color="primary">
								<KeyboardBackspace />
							</IconButton>
						</EasTooltip>
						<Typography variant="h6" color="primary" fontWeight="bold">
							{title}
						</Typography>
						{loading && <CircularProgress size={18} />}
					</Box>
					<Button
						id="shopify-store-edit-button"
						variant="contained"
						color="primary"
						size="small"
						startIcon={<Edit />}
						disabled={isInitialLoading || storeIdNum == null}
						onClick={() => setIsEditOpen(true)}
					>
						Edit
					</Button>
				</Box>

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 1.5,
						pl: 1,
						pr: 1,
						pt: 0.5,
					}}
				>
					{/* Section A: top row */}
					{/* <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<EasParamsBox<TShopifyStoreFlat>
								items={flat ?? ({} as TShopifyStoreFlat)}
								boxStructure={storeBoxStructure}
								columnNumber={1}
								editable={false}
								loading={isInitialLoading}
								fontSize="s"
							/>
						</Box>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<EasParamsBox<TShopifyStoreFlat>
								items={flat ?? ({} as TShopifyStoreFlat)}
								boxStructure={applicationBoxStructure}
								columnNumber={1}
								editable={false}
								loading={isInitialLoading}
								fontSize="s"
							/>
						</Box>
					</Box> */}

					{/* Section B + C */}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							gap: 1.5,
							width: '100%',
							flex: '1 1 auto',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 1.5,
								flex: 1,
								minWidth: 0,
							}}
						>
							<EasParamsBox<TShopifyStoreFlat>
								items={flat ?? ({} as TShopifyStoreFlat)}
								boxStructure={storeBoxStructure}
								columnNumber={1}
								editable={false}
								loading={isInitialLoading}
								fontSize="s"
							/>

							<EasParamsBox<TShopifyStoreFlat>
								items={flat ?? ({} as TShopifyStoreFlat)}
								boxStructure={salesChannelsBoxStructure}
								columnNumber={1}
								editable={false}
								loading={isInitialLoading}
								fontSize="s"
								banner={salesChannelsBoxStructure.banner?.message}
							/>
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 1.5,
								flex: 1,
								minWidth: 0,
							}}
						>
							<EasParamsBox<TShopifyStoreFlat>
								items={flat ?? ({} as TShopifyStoreFlat)}
								boxStructure={applicationBoxStructure}
								columnNumber={1}
								editable={false}
								loading={isInitialLoading}
								fontSize="s"
							/>
							<EasParamsBox<TShopifyStoreFlat>
								items={flat ?? ({} as TShopifyStoreFlat)}
								boxStructure={easAuthBoxStructure}
								columnNumber={1}
								editable={false}
								loading={isInitialLoading}
								fontSize="s"
							/>
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 1.5,
								flex: 1,
								minWidth: 0,
							}}
						>
							<EasParamsBox<TShopifyStoreFlat>
								items={flat ?? ({} as TShopifyStoreFlat)}
								boxStructure={easParametersBoxStructure}
								columnNumber={1}
								editable={false}
								loading={isInitialLoading}
								fontSize="s"
							/>
						</Box>
					</Box>
				</Box>
			</Box>

			<EditShopifyStoreDialog
				open={isEditOpen}
				storeId={storeIdNum}
				onClose={() => setIsEditOpen(false)}
			/>
		</Box>
	);
};
